import { useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface TipData {
  /** What the hovered mark is. */
  label: string;
  /** The number the mark stands for, already formatted. */
  value: string;
  /** Optional second line: a share, a date, what clicking would do. */
  note?: string;
  /** The mark's own colour, echoed as a swatch so the tip is traceable. */
  color?: string;
}

/**
 * One tooltip shared by every chart.
 *
 * The charts used to lean on the browser's own `title` tooltip, which appears
 * about a second late, cannot be styled, and never shows at all on a mark the
 * pointer only crosses. This one follows the cursor immediately and reads in
 * the dashboard's own voice.
 *
 * It renders through a portal because every chart sits inside a card with
 * `overflow:hidden`, which would otherwise clip a tip near the card's edge.
 */
export function useChartTip() {
  const [tip, setTip] = useState<{ x: number; y: number; d: TipData } | null>(null);

  const bind = (d: TipData) => ({
    onMouseEnter: (e: MouseEvent) => setTip({ x: e.clientX, y: e.clientY, d }),
    onMouseMove: (e: MouseEvent) => setTip({ x: e.clientX, y: e.clientY, d }),
    onMouseLeave: () => setTip(null),
  });

  let tipNode: ReactNode = null;
  if (tip) {
    /* Flip the tip across the cursor near the edges rather than measuring it,
       so it never sits half outside the window. */
    const flipX = tip.x > window.innerWidth - 220;
    const flipY = tip.y > window.innerHeight - 110;
    tipNode = createPortal(
      <div
        className="charttip" role="tooltip"
        style={{
          left: tip.x,
          top: tip.y,
          transform: `translate(${flipX ? "calc(-100% - 14px)" : "14px"}, ${
            flipY ? "calc(-100% - 12px)" : "12px"})`,
        }}
      >
        <span className="ct-lab">
          {tip.d.color && <i className="ct-sw" style={{ background: tip.d.color }} />}
          {tip.d.label}
        </span>
        <span className="ct-val">{tip.d.value}</span>
        {tip.d.note && <span className="ct-note">{tip.d.note}</span>}
      </div>,
      document.body,
    );
  }

  return { bind, tipNode };
}
